import { Form, Input, FormData, Fieldset, Radio, Select, FormRow, FormCol, Checkbox } from '@alxgrn/react-form';
import { SelectOption } from '@alxgrn/react-form/dist/components/Select';
import CheckboxList, { CheckboxListOption } from '@alxgrn/react-form/dist/components/CheckboxList';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { authUpdateUser } from '../store/authSlice';
import { Attendee, Language } from '../types';
import { get, put } from '../utils/api';
import Modal from '../components/common/Modal';
import Translate from '../components/common/Translate';
import { getAlpha2Codes, getCountryName } from '../utils/countries';

const GEO_RU = [{ value: 'federal', label: 'Федеральная'},{ value: 'regional', label: 'Региональная' }];
const GEO_EN = [{ value: 'federal', label: 'Federal'},{ value: 'regional', label: 'Regional' }];
const LIMIT_LONG = 500;
const LIMIT_SHORT = 100;

export interface InfoEditProps {
    isOpen: boolean;
    onClose: () => void;
};

const InfoEdit: FC<InfoEditProps> = ({ isOpen, onClose }) => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const geo = i18n.language === 'en' ? GEO_EN : GEO_RU;
    const user = useAppSelector(store => store.auth.user);
    const event = useAppSelector(store => store.event);
    const dispatch = useAppDispatch();
    const [ error, setError ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const [ email, setEmail ] = useState(user?.email ?? '');
	const [ company_ru, setCompanyRu ] = useState(user?.company_ru ?? '');
	const [ company_en, setCompanyEn ] = useState(user?.company_en ?? '');
    const [ position_ru, setPositionRu ] = useState(user?.position_ru ?? '');
    const [ position_en, setPositionEn ] = useState(user?.position_en ?? '');
	const [ city_ru, setCityRu ] = useState(user?.city_ru ?? '');
	const [ city_en, setCityEn ] = useState(user?.city_en ?? '');
	const [ description_ru, setDescriptionRu ] = useState(user?.description_ru ?? '');
	const [ description_en, setDescriptionEn ] = useState(user?.description_en ?? '');
    const [ geography, setGeography ] = useState<string>(user?.geography ?? 'federal');
    const [ cntrs, setCntrs ] = useState<SelectOption[]>([]);
	const [ country, setCountry ] = useState(user?.country ?? 'RU');
    const [ isTranslating, setIsTranslating ] = useState(false);
	const [ languages, setLanguages ] = useState<CheckboxListOption[]>([]);
    const [ direct_import, setDirectImport ] = useState(user?.direct_import ?? false);
    const [ products_type, setProductsType ] = useState<CheckboxListOption[]>([]);

    // Сбрасываем данные при открытии закрытии
    useEffect(() => {
        setEmail(user?.email ?? '');
        setCompanyRu(user?.company_ru ?? '');
        setCompanyEn(user?.company_en ?? '');
        setPositionRu(user?.position_ru ?? '');
        setPositionEn(user?.position_en ?? '');
        setCityRu(user?.city_ru ?? '');
        setCityEn(user?.city_en ?? '');
        setDescriptionRu(user?.description_ru ?? '');
        setDescriptionEn(user?.description_en ?? '');
        setGeography(user?.geography ?? 'federal');
        setCountry(user?.country ?? 'RU');
        setDirectImport(user?.direct_import ?? false);
    }, [ user, isOpen ]);

	// Запрашиваем список языков мероприятия
	useEffect(() => {
		const fetchLanguages = async () => {
			setLanguages([]);
			if(!user?.event_id) return;
			const result = await get('/langtoevent', { event_id: user.event_id });
            if(!result.data) return;
			const options: CheckboxListOption[] = [];
			(result.data.languages as Language[]).forEach(lang => {
				options.push({
					label: lang[name],
					value: lang.id,
					checked: user ? user.languages.includes(lang.id) : false,
				});
			});
			setLanguages(options);
		};

		if (isOpen) fetchLanguages();
	}, [ user, name, isOpen ]);

    // Сортируем массив для выбора страны
    // Россия всегда на первом месте
    useEffect(() => {
        const codes = getAlpha2Codes();
        const cntrs: SelectOption[] = [];
        Object.keys(codes).forEach(code => {
            cntrs.push({ value: code, option: getCountryName(code, lang) });
        });
        cntrs.sort((a, b) => {
            if(a.value === 'RU') return -1;
            if(b.value === 'RU') return  1;
            return (a.option).localeCompare(b.option);
        });
        setCntrs(cntrs);
    }, [lang]);

    /**
     * Подготовим чекбоксы для выбора типа закупок/продукции
     */
    useEffect(() => {
        const products_type = user?.products_type ?? [ false, false, false ];
        setProductsType([{
            label: t('attendee.products_type_stm'),
            value: 'stm',
            checked: products_type[0],
        },{
            label: t('attendee.products_type_brand'),
            value: 'brand',
            checked: products_type[1],
        },{
            label: t('attendee.products_type_dist'),
            value: 'dist',
            checked: products_type[2],
        }]);
    }, [ user, t, isOpen ]);
    /**
     * Проверка того что данные формы были изменены
     */
    const wasChanged = (): boolean => {
        if (!user) return false;
        // проверка смены списка языков
        const a1 = Array.from(languages.filter(l => l.checked).map(l => l.value)).sort();
        const a2 = Array.from(user.languages).sort();
        const isLang = a1.length === a2.length && a1.every(function(v, i) { return v === a2[i] });
        // обычные текстовые поля и вычисленная смена языка
        return (
            email !== (user.email ?? '') ||
            city_en !== (user.city_en ?? '') ||
            city_ru !== (user.city_ru ?? '') ||
            company_en !== (user.company_en ?? '') ||
            company_ru !== (user.company_ru ?? '') ||
            position_en !== (user.position_en ?? '') ||
            position_ru !== (user.position_ru ?? '') ||
            description_en !== (user.description_en ?? '') ||
            description_ru !== (user.description_ru ?? '') ||
            country !== (user.country ?? '') ||
            geography !== (user.geography ?? '') ||
            products_type[0].checked !== user.products_type[0] ||
            products_type[1].checked !== user.products_type[1] ||
            products_type[2].checked !== user.products_type[2] ||
            !isLang
        );
    };
    /**
     * Отправка данных на сервер
     */
    const onSubmit = async (data: FormData) => {
		setIsLoading(true);
        setError('');
        
        // если тип закупок/продукции придет, то он будет в виде массива из слов, надо его переделать в формат хранения
        const data_products: string[] = data.products_type ? data.products_type as string[] : [];
        const products_type: boolean[] = [
            data_products.includes('stm'),
            data_products.includes('brand'),
            data_products.includes('dist'),
        ];

		const result = await put('/attendees/' + user?.id, { ...data, products_type });
		if(result.error) {
			setError(result.error.text[lang]);
		} 
        if(result.data) {
			dispatch(authUpdateUser(result.data.attendee as Attendee));
            onClose();
		}
        setIsLoading(false);
	};

    const beforeClose = () => {
        if (isLoading || isTranslating) return;
        if (wasChanged() && !window.confirm(t('profile.edit.discard_confirm') ?? undefined)) return;
        onClose();
    };

    if(!user) return <div className='error'>{t('common.nouser')}</div>;

    return (
		<Modal isOpen={isOpen} onClose={beforeClose}>
            <h1>{t('profile.title.edit')}</h1>
            {isLoading ? <div>{t('common.loading')}</div> :
            <Form
                error={error}
                submit={isTranslating ? undefined : t('profile.edit.submit')}
                onSubmit={onSubmit}
            >
                <FormRow>
                    <FormCol>
                        <Input
                            id='email'
                            type='text'
                            value={email}
                            onChange={setEmail}
                            label={t('profile.edit.email')}
                            disabled={true}
                        />
                    </FormCol>
                    <FormCol>
                        <Select
                            id='country'
                            label={t('profile.edit.country')}
                            value={country}
                            options={cntrs}
                            onChange={setCountry}
                            placeholder='-'
                            disabled={isTranslating}
                        />
                    </FormCol>
                </FormRow>
                <FormRow>
                    <FormCol>
                        <Input
                            id='company_ru'
                            type='text'
                            value={company_ru}
                            onChange={setCompanyRu}
                            label={t('profile.edit.company_ru')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={company_ru}
                                        text={company_en}
                                        from='en'
                                        limit={LIMIT_SHORT}
                                        setText={setCompanyRu}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                    <FormCol>
                        <Input
                            id='company_en'
                            type='text'
                            value={company_en}
                            onChange={setCompanyEn}
                            label={t('profile.edit.company_en')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={company_en}
                                        text={company_ru}
                                        from='ru'
                                        limit={LIMIT_SHORT}
                                        setText={setCompanyEn}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                </FormRow>
                <FormRow>
                    <FormCol>
                        <Input
                            id='position_ru'
                            type='text'
                            value={position_ru}
                            onChange={setPositionRu}
                            label={t('profile.edit.position_ru')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={position_ru}
                                        text={position_en}
                                        from='en'
                                        limit={LIMIT_SHORT}
                                        setText={setPositionRu}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                    <FormCol>
                        <Input
                            id='position_en'
                            type='text'
                            value={position_en}
                            onChange={setPositionEn}
                            label={t('profile.edit.position_en')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={position_en}
                                        text={position_ru}
                                        from='ru'
                                        limit={LIMIT_SHORT}
                                        setText={setPositionEn}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                </FormRow>
                <FormRow>
                    <FormCol>
                        <Input
                            id='city_ru'
                            type='text'
                            value={city_ru}
                            onChange={setCityRu}
                            label={t('profile.edit.city_ru')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={city_ru}
                                        text={city_en}
                                        from='en'
                                        limit={LIMIT_SHORT}
                                        setText={setCityRu}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                    <FormCol>
                        <Input
                            id='city_en'
                            type='text'
                            value={city_en}
                            onChange={setCityEn}
                            label={t('profile.edit.city_en')}
                            disabled={isTranslating}
                            limit={LIMIT_SHORT}
                            bottom={<Translate
                                        current={city_en}
                                        text={city_ru}
                                        from='ru'
                                        limit={LIMIT_SHORT}
                                        setText={setCityEn}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                </FormRow>
                <FormRow>
                    <FormCol>
                        <Input
                            id='description_ru'
                            type='textarea'
                            value={description_ru}
                            onChange={setDescriptionRu}
                            label={t('profile.edit.description_ru')}
                            disabled={isTranslating}
                            limit={LIMIT_LONG}
                            bottom={<Translate
                                        current={description_ru}
                                        text={description_en}
                                        from='en'
                                        limit={LIMIT_LONG}
                                        setText={setDescriptionRu}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                    <FormCol>
                        <Input
                            id='description_en'
                            type='textarea'
                            value={description_en}
                            onChange={setDescriptionEn}
                            label={t('profile.edit.description_en')}
                            disabled={isTranslating}
                            limit={LIMIT_LONG}
                            bottom={<Translate
                                        current={description_en}
                                        text={description_ru}
                                        from='ru'
                                        limit={LIMIT_LONG}
                                        setText={setDescriptionEn}
                                        setIsTranslating={setIsTranslating}
                                    />}
                        />
                    </FormCol>
                </FormRow>

                {(event?.use_direct_import && country !== 'RU' && user.role === 'seller') &&
                <FormRow>
                    <FormCol>
                        <Fieldset
                            legend={t('profile.edit.direct_import_legend')}
                        >
                            <Checkbox
                                id='direct_import'
                                label={t('profile.edit.direct_import_label')}
                                checked={direct_import}
                                onChange={setDirectImport}
                                disabled={isTranslating}
                            />
                        </Fieldset>
                    </FormCol>
                </FormRow>}

                {event?.use_products_type &&
                <FormRow>
                    <FormCol>
                        <CheckboxList
                            id='products_type'
                            label={t(`attendee.${user.role}.products_type`)}
                            required={true}
                            options={products_type}
                            onChange={setProductsType}
                            disabled={isTranslating}
                        />
                    </FormCol>
                </FormRow>}

                <FormRow>
                    <FormCol>
                        <Fieldset
                            legend={t('profile.edit.geography')}
                        >
                            <Radio
                                id='geography'
                                value={geography}
                                options={geo}
                                onChange={setGeography}
                                disabled={isTranslating}
                            />
                        </Fieldset>
                    </FormCol>
                    <FormCol>
                        <CheckboxList
                            id='languages'
                            label={t('profile.edit.languages')}
                            required={true}
                            options={languages}
                            onChange={setLanguages}
                            disabled={isTranslating}
                        />
				    </FormCol>
                </FormRow>
            </Form>}
		</Modal>
	);
};

export default InfoEdit;
